import { getProduct } from '@commerce/api/operations'
import { Product } from '@commerce/types'
import { ProductMetafields } from '@commerce/types/product'
import { Breadcrumbs, Layout, Loader } from '@components/common'
import { SauceGallery, SauceProductGallery } from '@components/common/Sauce'
import { ProductView } from '@components/product'
import { Container } from '@components/ui'
import productBuildPaths from '@config/productBuildPaths.json'
import { getColourGroupFromTags } from '@lib/colors'
import getGlobalStaticProps from '@lib/globalStaticProps'
import {
  COLOUR_CARD_OPTION_LABEL,
  GARDEN_POT_PAINT_OPTION_LABEL,
  HIDDEN_TAG,
  HIDE_SAUCE_TAG,
  PAINT_OPTION_LABEL,
  PRODUCT_TYPE_COLOUR,
  PRODUCT_TYPE_TOOLS,
  SAMPLE_POT_OPTION_LABEL,
  findBundleInfoFromMetafield,
  orderMetafieldByKey,
} from '@lib/helpers/general'
import { getConfigByLocale } from '@lib/locale'
import { SliceZone } from '@prismicio/react'
import { components } from '@slices/index'
import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { createClient, findPrismicUidFromTags, getDocByUid } from 'prismicio'
import { Fragment } from 'react'

type ProductPath = { params: { slug: string }; locale: string }

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const productPromise = getProduct(locale, params!.slug, 'no-cache')
  const { product }: { product: Product.Product } = await productPromise
  if (!product || product?.tags?.includes(HIDDEN_TAG)) {
    return { notFound: true }
  }
  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
    'product',
    'cart',
  ])
  let productSiblings: Product.Product[] = []

  const metafieldsWithOrder = orderMetafieldByKey(product?.metafields!)
  const switchLabel = (index: number) => {
    switch (index) {
      case 0: {
        return COLOUR_CARD_OPTION_LABEL
      }
      case 1: {
        return SAMPLE_POT_OPTION_LABEL
      }
      case 2: {
        return GARDEN_POT_PAINT_OPTION_LABEL
      }
      case 3: {
        return PAINT_OPTION_LABEL
      }
    }
  }

  const getProductSiblings: any = metafieldsWithOrder?.map(
    async (v: ProductMetafields, index: number) => {
      try {
        await getProduct(locale, v.value, 'no-cache').then(
          (res) => (productSiblings[index] = res.product)
        )
        productSiblings = productSiblings.map(
          (sibling: any, index: number) => ({
            ...sibling,
            siblingOptionLabel: switchLabel(index),
          })
        )
      } catch (err) {
        console.log(err)
      }
    }
  )

  if (product.productType === PRODUCT_TYPE_COLOUR)
    await Promise.all(getProductSiblings)

  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  let doc = null
  try {
    const prismicDocUId = findPrismicUidFromTags(product.tags)
    if (prismicDocUId) {
      doc = await getDocByUid('colour-pdp', prismicDocUId, client, locale)
    }
  } catch (e) {}
  let colourProductContent = null
  try {
    colourProductContent = await getDocByUid(
      'colour-product-content',
      'colour-product-content',
      client,
      locale
    )
  } catch (e) {
    console.log(e)
  }

  return {
    props: {
      ...globalProps,
      ...translations,
      pages: [],
      product,
      productSiblings: productSiblings.filter((p) => p),
      colourProductContent,
      categories: [],
      doc,
    },
    revalidate: 86400, // Refresh every 24 hours
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  if (process?.env?.NODE_ENV === 'production') {
    return {
      paths: [],
      fallback: true,
    }
  }
  const paths: ProductPath[] = productBuildPaths

  // we stored some paths in json so we do not have to fetch it all the time from algolia
  // const promises = locales?.map(async (locale) => {
  //   const products = await getAllProductsFromAlgolia(locale)
  //   products
  //     .flat()
  //     .slice(0, 50)
  //     .map((p) => {
  //       paths.push({
  //         params: {
  //           slug: p.handle,
  //         },
  //         locale,
  //       })
  //     })
  // })
  // await Promise.all(promises!)

  // fs.writeFile('./config/productBuildPaths.json')

  return {
    paths,
    fallback: true,
  }
}
export default function Slug({
  product,
  doc,
  productSiblings,
  colourProductContent,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const { data }: any = doc || {}
  const p: Product.Product = product
  let firstCrum = {
    label: 'colour-lab',
    link: '/collection/colour-lab',
  }
  if (p?.productType === PRODUCT_TYPE_TOOLS)
    firstCrum = {
      label: 'tools-supplies',
      link: '/collection/tools-and-supplies',
    }
  const crumbs = [
    firstCrum,
    {
      label: p?.name,
      link: router.asPath,
    },
  ]
  if (router.isFallback) return <Loader title="product-loader" />
  // if (!data || !product) {
  //   return <Error code={404} />
  // }
  const bundleInfo = findBundleInfoFromMetafield(product.metafields)
  const isBundleProduct = !!bundleInfo
  const showBreadCrumb =
    p?.productType === PRODUCT_TYPE_COLOUR ||
    p?.productType === PRODUCT_TYPE_TOOLS ||
    isBundleProduct
  const colourGroup = getColourGroupFromTags(p?.tags)
  const sauceId = getConfigByLocale(router.locale!, 'sauceId')
  const displaySauce = !p?.tags?.includes(HIDE_SAUCE_TAG)
  return (
    <>
      <Head>
        <meta name="sauce-product-handle" content={p.handle} />
      </Head>
      {showBreadCrumb && (
        <div className="bg-brand-light-grey">
          <Container className="py-2 md:pt-6 md:pb-4">
            <Breadcrumbs crumbs={crumbs} />
          </Container>
        </div>
      )}
      <div className="lg:pb-8">
        <ProductView
          product={product}
          productSiblings={productSiblings}
          prismicContent={colourProductContent}
        />
      </div>

      {sauceId && displaySauce && (
        <Fragment key={`${p.handle}-${router.locale}`}>
          <div className="lg:pt-8">
            <div className="bg-brand-light-grey pb-8">
              <Container>
                <SauceProductGallery handle={p.handle!} />
              </Container>
            </div>
            {colourGroup && (
              <div className="bg-white pt-16 pb-8">
                <Container>
                  <SauceGallery
                    galleryHandle={colourGroup!}
                    handle={p.handle!}
                  />
                </Container>
              </div>
            )}
          </div>
        </Fragment>
      )}
      <SliceZone
        slices={data?.slices}
        components={components}
        context={product}
      />
      <style jsx global>
        {`
          .snptwdgt__headr {
            padding-bottom: 32px !important;
          }
          .snptwdgt__headng {
            text-align: left !important;
          }
          .snptwdgt__subheadng {
            text-align: left !important;
          }
        `}
      </style>
    </>
  )
}

Slug.Layout = Layout
